import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from "@mui/material/CircularProgress";
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Typography from '@mui/material/Typography';
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Radio from '@mui/material/Radio';
import { IconButton, InputAdornment, Link } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import Autocomplete from "@mui/material/Autocomplete";
import VisibilityIcon from "../../../../assets/view_eyeIcon.svg";
import './CreateUsers.css';

// API & Authorization
import { updateUser, fetchUserData, getRoleOrgs, getUserRoles, fetchUserOrgProjects } from '../../../../apis/users.api';
import { AuthContext } from '../../../../AuthContext';
import { getOrgRoles } from '../../../../apis/organisations.api';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';

// Custom Popper component
const CustomPopper = styled(Popper)(({ theme }) => ({
  width: 'auto !important',
  minWidth: '49%',
  zIndex: theme.zIndex.modal,
}));

function EditUsers({ 
    openEditUsers, 
    setOpenEditUsers, 
    setIsToastOpen, 
    setToastMessage, 
    selectedUser, 
    setLoading,
    setErrorCode 
}) {

    const {authData} = useContext(AuthContext)
    const {access_token, token_type} = authData;
    const queryClient = useQueryClient();
    const [showPassword, setShowPassword] = useState(false);
    const [isPasswordDisabled, setIsPasswordDisabled] = useState(true);
    const [orgId, setOrgId] = useState({"org_id_list": [0], "roles": [0]});
    const [orgData, setOrgData] = useState(null);
    const [projData, setProjData] = useState(null);
    
    const [formValues, setFormValues] = useState({
        name: "",
        email: "",
        password: null,
        organisations: [{name: null, role: null, isDefault: false}],
        projects: [{name: null, role: null, isDefault: false}]
    });

    useEffect(() => {
        console.log("Form values use Effect",formValues);
        const orgIds = formValues?.organisations
            ?.map(org => findOrgId(org?.name))
            ?.filter(orgId => orgId !== 0);
        const roleIds = formValues?.organisations
            ?.map(org => findOrgRoleId(org?.role))
            ?.filter(roleId => roleId !== 0);
        setOrgId({ org_id_list: orgIds, roles: roleIds });
    }, [formValues?.organisations]);

    // Memoize orgId to prevent unnecessary re-renders
    const memoizedOrgId = useMemo(() => orgId, [JSON.stringify(orgId)]);

    const { data: getUsersData, isLoading: loadingOrg } = useQuery({
        queryKey: ['user', { selectedUser, access_token, token_type }],
        queryFn: fetchUserData,
        enabled: !!selectedUser && !!access_token && !!token_type
    });

    // Handle user data processing when getUsersData changes
    useEffect(() => {
        if (getUsersData) {
            setFormValues((prevValues) => ({
                ...prevValues,
                name: getUsersData?.name,
                email: getUsersData?.email,
                organisations: getUsersData?.organisations?.length > 0 ? getUsersData?.organisations?.map(org => ({
                    name: org?.name,
                    role: org?.role_name,
                    isDefault: org?.is_default
                })) : [{ name: null, role: null, isDefault: false }],
                projects: getUsersData?.projects?.length > 0 ? getUsersData?.projects?.map(proj => ({
                    name: proj?.name,
                    role: proj?.role_name,
                    isDefault: proj?.is_default
                })) : [{ name: null, role: null, isDefault: false }]
            }));
        }
    }, [getUsersData]);

    useEffect(()=>{
        
            const getUserdata = async ()=>{
                try{
                    const data = await fetchUserData({ selectedUser, access_token, token_type });
                    setFormValues((prevValues) => ({
                        ...prevValues,
                        name: data?.name,
                        email: data?.email,
                        //password: data?.password,
                        organisations: data?.organisations?.length > 0 ? data?.organisations?.map(org => ({
                        name: org?.name,
                        role: org?.role_name,
                        isDefault: org?.is_default
                        })) : [{ name: null, role: null, isDefault: false }],
                        projects: data?.projects?.length > 0 ? data?.projects?.map(proj => ({
                        name: proj?.name,
                        role: proj?.role_name,
                        isDefault: proj?.is_default
                        })) : [{ name: null, role: null, isDefault: false }]
                    }));
                }catch(err){
                    console.error("error first load",err);
                }
            }
            getUserdata();        
    },[]);

    const { data: getOrgs, isLoading: loadingOrgs } = useQuery({
        queryKey: ['roleOrgs', {access_token, token_type}], 
        queryFn: getRoleOrgs,
        enabled: !!access_token
    });

    // Handle org data processing when getOrgs changes
    useEffect(() => {
        if (getOrgs) {
            setOrgData(getOrgs);
        }
    }, [getOrgs]);

    const { data: getProjs, isLoading: loadingProjs } = useQuery({
        queryKey: ['roleProjs', { orgId : memoizedOrgId, access_token, token_type }],
        queryFn: fetchUserOrgProjects,
        enabled: !!access_token && memoizedOrgId?.org_id_list?.length > 0 && memoizedOrgId?.org_id_list?.[0] !== 0
    });

    // Handle project data processing when getProjs changes
    useEffect(() => {
        if (getProjs) {
            setProjData(getProjs);
        }
    }, [getProjs]);

    const { data: getUserOrgRoles, isLoading: loadingUserOrgRoles } = useQuery({
        queryKey: ['userOrgRoles', {role_type:"org_role", access_token, token_type}],
        queryFn: getUserRoles,
        enabled: !!access_token
    });
    const { data: getUserProjRoles, isLoading: loadingUserProjRoles } = useQuery({
        queryKey: ['userProjRoles', {role_type:"project_role", access_token, token_type}],
        queryFn: getUserRoles,
        enabled: !!access_token
    });

    const handleAddNewOrg = () => {
        setFormValues({
            ...formValues,
            organisations: [...formValues.organisations, { name: null, role: null, isDefault: false}]
        });
    };

    const handleAddNewUserProject = () => {
        setFormValues({
            ...formValues,
            projects: [...formValues.projects, { name: null, role: null, isDefault: false }]
        });
    };

    const handleChangeOrganization = (event, index, field, value) => {
        const newOrganisations = [...formValues.organisations];
        newOrganisations[index][field] = value;
        setFormValues({
            ...formValues,
            organisations: newOrganisations
        });
    };

    const handleChangeProject = (event, index, field, value) => {
        const newProjects = [...formValues.projects];
        newProjects[index][field] = value;
        setFormValues({
            ...formValues,
            projects: newProjects
        });
    };

    const handleRemoveUser = (index) => {
        const newOrganisations = formValues?.organisations.filter((_, i) => i !== index);
        setFormValues({
            ...formValues,
            organisations: newOrganisations?.length === 0 ? [{ name: null, role: null, isDefault:false }] : newOrganisations
        });
    };

    const handleRemoveUserProject = (index) => {
        const newProjects = formValues?.projects.filter((_, i) => i !== index);
        setFormValues({
            ...formValues,
            projects: newProjects?.length === 0 ? [{ name: null, role: null, isDefault:false }] : newProjects
        });
    };

    const findOrgId = (orgName) => {
        const org = orgData?.find((o) => o.name === orgName);
        return org ? org.org_id : 0;
    };

    const findProjId = (projName) => {
        const proj = getProjs?.find((p) => p.name === projName);
        return proj ? proj.project_id : 0;
    };

    const findOrgRoleId = (roleName) => {
        const role = getUserOrgRoles?.find((r) => r.role_name === roleName);
        return role ? role.role_id : 0;
    };
    const findProjRoleId = (roleName) => {
        const role = getUserProjRoles?.find((r) => r.role_name === roleName);
        return role ? role.role_id : 0;
    };

    const mutation = useMutation({
        mutationFn: updateUser,
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            setToastMessage('User has been updated successfully');
            setErrorCode(res?.status);
            setLoading(false);
            setIsToastOpen(true);
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
            setToastMessage(`Error: ${errorMessage}`);
            setErrorCode(error?.response?.status);
            setLoading(false);
            setIsToastOpen(true);
        }
    });

    const EditUserFormSubmit = (event) => {
        event.preventDefault();
        console.log("formvalues",formValues);
        const payload = {
            name: formValues.name,
            email: formValues.email,
            password: formValues.password,
            organisations: formValues.organisations.map((org) => {
                return {
                    org_id: findOrgId(org.name),
                    role_id: findOrgRoleId(org.role),
                    is_default : org.isDefault
                }
            }),
            projects: formValues.projects
                .filter(project => project.name !== null && project.role !== null)
                .map(project => ({ 
                    project_id: findProjId(project.name), 
                    role_id: findProjRoleId(project.role), 
                    is_default : project.isDefault 
                }))
        };
        
        setLoading(true);
        setOpenEditUsers(false);
        mutation.mutate({ selectedUser, payload, access_token, token_type });
    };

    const togglePasswordDisable = () => {
        setIsPasswordDisabled(!isPasswordDisabled);
        setShowPassword(false);
    };
    
    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleDefaultOrgSelected = (e) => {
        try{
            const selectedIndex = e.target.value;
            let currOrganisations = [...formValues.organisations];
            currOrganisations = currOrganisations.map((i,index)=>{
                if(index === Number(selectedIndex)){
                    i.isDefault = true;
                    return i;
                }else{
                    i.isDefault = false;
                    return i;
                }  
            })
            setFormValues({
                ...formValues,
                organisations: currOrganisations
            });
            
        }catch(err){
            console.error("select defgault Org",err);
        }
    }

    const handleDefaultProjectSelected = (e) => {
        try{
            const selectedIndex = e.target.value;
            const currProjects = [...formValues.projects].map((i,index)=>{
                if(index === Number(selectedIndex)){
                    i.isDefault = true;
                    return i;
                }else{
                    i.isDefault = false;
                    return i;
                }  
            })
            setFormValues({
                ...formValues,
                projects: currProjects
            });
        }catch(err){
            console.error("select default Project",err);
        }
    }

    return (
        <>
        <Modal
            aria-labelledby="transition-modal-title"
            aria-describedby="transition-modal-description"
            open={openEditUsers}
            onClose={() => setOpenEditUsers(false)}
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            slotProps={{
                backdrop: {
                    timeout: 500,
                },
            }}
            className="createUserModal"
        >
            <Fade in={openEditUsers}>
                <Box className="createUserModalClass">
                    <div className="createUserModalHeader">
                        <Typography
                            id="transition-modal-title"
                            variant="h6"
                            component="h2"
                            className="createUserModalTitle"
                        >
                            Edit User
                        </Typography>
                        <span
                            className="createUserModalClose"
                            onClick={() => setOpenEditUsers(false)}
                        >
                            &times;
                        </span>
                    </div>
                    <form className="g-3" onSubmit={(e)=>EditUserFormSubmit(e)}>
                        <div className="createUserModalBody">
                            <div className='superAdminTitle'>User Information</div>
                            <div className="d-flex" style={{ margin: '16px 0', position: 'relative' }}>
                                <div className="col-6" style={{ paddingRight: '12px' }}>
                                    <label htmlFor="org-name" className="form-label">
                                        User Name<span style={{color: 'red'}}>&#65290;</span>
                                    </label>
                                    <TextField
                                        required
                                        id="org-name"
                                        placeholder="User name"
                                        sx={{ width: "100%" }}
                                        onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
                                        value={formValues?.name}
                                    />
                                </div>
                                <div className="col-4" style={{ paddingRight: '12px' }}>
                                    <label htmlFor="email-id" className="form-label">
                                        Email ID<span style={{color: 'red'}}>&#65290;</span>
                                    </label>
                                    <TextField
                                        required
                                        id="email-id"
                                        placeholder="Email ID"
                                        sx={{ width: "100%" }}
                                        onChange={(e) => setFormValues({ ...formValues, email: e.target.value })}
                                        value={formValues?.email}
                                    />
                                </div>
                            </div>
                            <div className="d-flex" style={{ margin: '16px 0', position: 'relative' }}>
                                <div className="col-6" style={{ paddingRight: '12px' }}>
                                    <label htmlFor="pswd" className="form-label">
                                        Password<span style={{color: 'red'}}>&#65290;</span>
                                    </label>
                                    <TextField
                                        required
                                        id="pswd"
                                        placeholder="Password"
                                        sx={{ width: "100%" }}
                                        type={showPassword ? "text" : "password"}
                                        onChange={(e) => setFormValues({ ...formValues, password: e.target.value })}
                                        // value={isPasswordDisabled ? '••••••••' : formValues?.password}
                                        disabled={isPasswordDisabled}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton edge="end" style={{marginRight:'0'}} onClick={togglePasswordDisable}>
                                                        <EditIcon />
                                                    </IconButton>
                                                    {!isPasswordDisabled && <IconButton edge="end" style={{marginRight:'0'}} onClick={togglePasswordVisibility}>
                                                        <img src={VisibilityIcon} alt="EditIcon"/>
                                                    </IconButton>}
                                                </InputAdornment >
                                            ),
                                        }}
                                    />
                                </div>
                            </div>
                            <div className='superAdminTitle'>Organizations</div>
                            <div style={{ margin: '16px 0' }}>
                                {formValues?.organisations?.map((user, index) => (
                                    <div key={index} style={{ marginTop: '16px' }}>
                                        <Link
                                            sx={{
                                                display: 'flex',
                                                textDecoration: 'none',
                                                justifyContent: 'flex-end',
                                                marginRight: '12px',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => handleRemoveUser(index)}
                                        >
                                            Remove
                                        </Link>
                                        <div className="d-flex" style={{position: 'relative'}}>
                                            {/* Set default */}
                                            <div className="col-2" style={{ paddingRight: '12px' }}>
                                                    <label htmlFor="role" className="form-label">Set Default</label>
                                                    <div>
                                                        <Radio
                                                            checked={user?.isDefault}
                                                            onChange={handleDefaultOrgSelected}
                                                            value={index}
                                                        />
                                                    </div>
                                            </div>
                                            <div className="col-5" style={{ paddingRight: '12px' }}>
                                                <label htmlFor="username" className="form-label">Organization Name{index === 0 && <span style={{color: 'red'}}>&#65290;</span>}</label>
                                                <Autocomplete
                                                    disablePortal
                                                    options={orgData?.filter(org => !org?.is_deleted) || []}
                                                    getOptionLabel={(option) => option?.name}
                                                    value={(orgData?.filter(org => !org?.is_deleted))?.find(u => u?.name === user?.name) || null}
                                                    onChange={(event, newValue) => {
                                                        handleChangeOrganization(event, index, 'name', newValue ? (newValue?.name) : null);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            required
                                                            placeholder="Enter Organization Name"
                                                            name="name"
                                                            sx={{ width: "100%" }}
                                                        />
                                                    )}
                                                    PopperComponent={(props) => <CustomPopper {...props} placement='bottom-start'/>}
                                                />
                                            </div>
                                            <div className="col-5" style={{ paddingRight: '12px' }}>
                                                <label htmlFor="role" className="form-label">Role{index === 0 && <span style={{color: 'red'}}>&#65290;</span>}</label>
                                                <Autocomplete
                                                    disablePortal
                                                    options={getUserOrgRoles || []}
                                                    getOptionLabel={(option) => option?.role_name}
                                                    value={getUserOrgRoles?.find(r => r?.role_name === user?.role) || null}
                                                    onChange={(event, newValue) => {
                                                        handleChangeOrganization(event, index, 'role', newValue ? (newValue?.role_name) : null);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            required
                                                            placeholder="Enter Role"
                                                            name="role"
                                                            sx={{ width: "100%" }}
                                                        />
                                                    )}
                                                />
                                            </div>


                                        </div>
                                    </div>
                                ))}
                                <Button
                                    type="button"
                                    variant="contained"
                                    color="success"
                                    onClick={handleAddNewOrg}
                                    sx={{
                                        textTransform: 'capitalize',
                                        fontFamily: 'open sans',
                                        fontWeight: 400,
                                        borderRadius: '0',
                                        m: '16px 0'
                                    }}
                                >
                                    Add more
                                </Button>
                            </div>
                            <div className='superAdminTitle'>Projects</div>
                            <div style={{ margin: '16px 0' }}>
                                {formValues?.projects?.map((user, index) => (
                                    <div key={index} style={{ marginTop: '16px' }}>
                                        <Link
                                            sx={{
                                                display: 'flex',
                                                textDecoration: 'none',
                                                justifyContent: 'flex-end',
                                                marginRight: '12px',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => handleRemoveUserProject(index)}
                                        >
                                            Remove
                                        </Link>
                                        <div className="d-flex" style={{position: 'relative'}}><div className="col-2" style={{ paddingRight: '12px' }}>
                                                <label htmlFor="role" className="form-label">Set Default</label>
                                                <div>
                                                <Radio
                                                    checked={user?.isDefault}
                                                    onChange={handleDefaultProjectSelected}
                                                    value={index}
                                                    name="radio-buttons"
                                                    inputProps={{ 'aria-label': user?.name }}
                                                    sx={{
                                                        '& .MuiSvgIcon-root': {
                                                          fontSize: 28,
                                                        },
                                                      }}
                                                />
                                                </div>
                                        </div>

                                            <div className="col-5" style={{ paddingRight: '12px' }}>
                                                <label htmlFor="project-name" className="form-label">Project Name</label>
                                                <Autocomplete
                                                    disablePortal
                                                    options={projData?.filter(proj => !proj?.is_deleted) || []}
                                                    getOptionLabel={(option) => option?.name}
                                                    value={(projData?.filter(proj => !proj?.is_deleted))?.find(u => u?.name === user?.name) || null}
                                                    onChange={(event, newValue) => {
                                                        handleChangeProject(event, index, 'name', newValue ? (newValue?.name) : null);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            placeholder="Enter Project Name"
                                                            name="name"
                                                            sx={{ width: "100%" }}
                                                        />
                                                    )}
                                                    PopperComponent={(props) => <CustomPopper {...props} placement='bottom-start'/>}
                                                />
                                            </div>
                                            <div className="col-5" style={{ paddingRight: '12px' }}>
                                                <label htmlFor="project-role" className="form-label">Role</label>
                                                <Autocomplete
                                                    disablePortal
                                                    options={getUserProjRoles || []}
                                                    getOptionLabel={(option) => option?.role_name}
                                                    value={getUserProjRoles?.find(r => r?.role_name === user?.role) || null}
                                                    onChange={(event, newValue) => {
                                                        handleChangeProject(event, index, 'role', newValue ? (newValue?.role_name) : null);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            placeholder="Enter Role"
                                                            name="role"
                                                            sx={{ width: "100%" }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                            
                                        </div>
                                    </div>
                                ))}
                                <Button
                                    type="button"
                                    variant="contained"
                                    color="success"
                                    onClick={handleAddNewUserProject}
                                    sx={{
                                        textTransform: 'capitalize',
                                        fontFamily: 'open sans',
                                        fontWeight: 400,
                                        borderRadius: '0',
                                        m: '16px 0'
                                    }}
                                >
                                    Add more
                                </Button>
                            </div>
                        </div>
                        <div className="createUserFormButtons">
                            <Button variant="outlined" size="large" onClick={() => setOpenEditUsers(false)}>
                                Cancel
                            </Button>
                            <Button variant="contained" className="btn-primary" size="large" type="submit">Save</Button>
                        </div>
                    </form>
                </Box>
            </Fade>
        </Modal>

        </>
    );
}

export default EditUsers;